import BigNumber from 'bignumber.js'
import { GAS_PRICE } from 'config'
import abiRanchFactory from 'config/abi/ranchFactory.json'
import abiRanch from 'config/abi/ranch.json'
import abiErc20 from 'config/abi/erc20.json'
import abiLpFactory from 'config/abi/lpFactory.json'
import abiISingleAddressValidator from 'config/abi/iSingleAddressValidator.json'
import abiPair from 'config/abi/pair.json'
import multicall from 'utils/multicall'
import {getBchPrice} from 'utils'
import { getBenSwapLpValidatorAddress, isAddressMatch, getLpFactoryAddress } from 'utils/addressHelpers'

const gasPriceInObj = GAS_PRICE?{gasPrice:GAS_PRICE}:{};
const favoriteCacheKey = "BenSwapFavoriteRanches";

export const getCreationFee = async (ranchFactoryContract) => {
    return ranchFactoryContract.methods.creationFee().call()
}

export const getFeeToken = async (ranchFactoryContract) => {
    return ranchFactoryContract.methods.feeToken().call()
}

export const getCreator = async (ranchFactoryContract, ranchAddress) => {
    return ranchFactoryContract.methods.getCreator(ranchAddress).call()
}

export const getRanchCountByCreator = async (ranchFactoryContract, userAddress) => {
    return ranchFactoryContract.methods.getRanchCount(userAddress).call()
}

export const getRanchCount = async (ranchFactoryContract) => {
    return ranchFactoryContract.methods.count().call()
}

export const create = async (ranchFactoryContract, token, lpToken, emissionPerBlock, startBlock, depositFeeBP, amount, account) => {
    try {
        return ranchFactoryContract.methods
        .createRanch(token, lpToken, account, emissionPerBlock, startBlock, depositFeeBP, amount)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}


export const getRanchAddressFromQuery = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('ranch');
}

let TokenPrices:any = null;
let TokenPricesTasks:any = [];
const AdditionalTokenPrices:any = {};
const WBCH = "0x3743eC0673453E5009310C727Ba4eaF7b3a1cc04";
const flexUSD = "0x7b2B3C5308ab5b2a1d9a94d20D35CCDf61e05b72";
const EBEN = "0x77CB87b57F54667978Eb1B199b28a0db8C8E1c0B";
const POPULAR_BASES = [
    WBCH, flexUSD, EBEN,
    "0x0b00366fBF7037E9d75E4A569ab27dAB84759302",   // LAW
    "0x265bD28d79400D55a1665707Fa14A72978FA6043",   // CATS
];
const WBCHLPs = [
    "0x0D4372aCc0503Fbcc7EB129e0De3283c348B82c3",   // EBEN
    "0xd55a9A41666108d10d31BAeEea5D6CdF3be6C5DD",   // LAW
    "0x47c58bEc061480bb6f6Ce076a69F4d236d841a71"    // CATS
];
const SPECIAL_DECIMALS = {
    "0x265bD28d79400D55a1665707Fa14A72978FA6043" : 2    // CATS
}

const retrievePopularTokenPrices = async () => {
    const tokenPrices:any = {};
    const tasks = [];

    // WBCH
    tasks.push(getBchPrice().then((text) =>{
        tokenPrices[WBCH] = new BigNumber(text);
    }));
    // LPs
    const calls1 = [];
    WBCHLPs.forEach((LP)=>{
        calls1.push({
            address: LP,
            name: 'token0'
        }, {
            address: LP,
            name: 'token1'
        }, {
            address: LP,
            name: 'getReserves'
        });
    });
    tasks.push(multicall(abiPair, calls1).then((res1)=> {
        for (let i=0; i< WBCHLPs.length; i++) {
            if (isAddressMatch(res1[i*3][0], WBCH)) {
                tokenPrices[res1[i*3+1][0]] = new BigNumber(res1[i*3+2][0]._hex).div(res1[i*3+2][1]._hex);
            } else {
                tokenPrices[res1[i*3][0]] = new BigNumber(res1[i*3+2][1]._hex).div(res1[i*3+2][0]._hex);
            }
        }
    }));

    TokenPricesTasks = tasks;
    await Promise.all(tasks).then(()=>{
        // Adjustments
        Object.keys(tokenPrices).forEach((token) => {
            if (!isAddressMatch(token, WBCH)) {
                tokenPrices[token] = tokenPrices[token].times(tokenPrices[WBCH]);
                if (SPECIAL_DECIMALS[token]) {
                    tokenPrices[token] = tokenPrices[token].div(new BigNumber(10).pow(18-SPECIAL_DECIMALS[token]));
                }
            }
        });

        tokenPrices[flexUSD] = new BigNumber(1);
        tokenPrices.timestamp = Date.now();

        if (tokenPrices[EBEN] && tokenPrices[EBEN].isGreaterThan(0)) TokenPrices = tokenPrices;
        TokenPricesTasks = [];
    });

    return tokenPrices;
}

// (Params) 
// tokens: {[tokenAddress]: { }, ...}
// tokenPrices: {[tokenAddress]: price<BigNumber>}
const tryGetPrices = async (tokens, baseTokenPrices) => {
    const lpFactory = getLpFactoryAddress();

    const validLps = [];
    const tokenAddresses = Object.keys(tokens);

    // Calls 1: Get LP addresses against popular base tokens
    const calls1 = [];
    for(let i=0; i<tokenAddresses.length; i++) {
        for (let j=0; j<POPULAR_BASES.length; j++) {
            calls1.push({
                address: lpFactory,
                name: 'getPair',
                params: [tokenAddresses[i], POPULAR_BASES[j]]
            })
        }
    }
    // Handle result for calls1
    const res1 = await multicall(abiLpFactory, calls1);
    for(let m=0; m<tokenAddresses.length; m++) {
        for (let n=0; n<POPULAR_BASES.length; n++) {
            const lp = res1[m * POPULAR_BASES.length + n][0];
            if (lp && (new BigNumber(lp.toLowerCase())).isGreaterThan(0)) 
                validLps.push(lp);
        }
    }

    // Calls 2: Get all valid LP details to retrieve the prices
    const calls2 = [];
    validLps.forEach((lp)=> {
        calls2.push({
            address: lp,
            name: 'token0'
        }, {
            address: lp,
            name: 'token1'
        }, {
            address: lp,
            name: 'getReserves'
        });
    });
    // Handle result for calls2
    const res2 = await multicall(abiPair, calls2);
    for(let o=0; o<validLps.length; o++) {
        const token0 = res2[o*3][0];
        const token1 = res2[o*3+1][0];
        if (baseTokenPrices[token0]) {
            if (tokens[token1] && !tokens[token1].price) {
                Object.assign(tokens[token1], {
                    price: baseTokenPrices[token0]
                            .times(res2[o*3+2][0]._hex).div(res2[o*3+2][1]._hex)
                            .div(new BigNumber(10).pow(SPECIAL_DECIMALS[token0]||18))
                            .times(new BigNumber(10).pow(tokens[token1].decimals))
                });
            }
        } else if (tokens[token0] && !tokens[token0].price) {
            Object.assign(tokens[token0], {
                price: baseTokenPrices[token1]
                        .times(res2[o*3+2][1]._hex).div(res2[o*3+2][0]._hex)
                        .div(new BigNumber(10).pow(SPECIAL_DECIMALS[token1]||18))
                        .times(new BigNumber(10).pow(tokens[token0].decimals))
            });
        }
    }
}

const CACHE = {};

export const getRanches = async (ranchFactoryContract, addresses, account) => {
    if (!addresses || !addresses.length) return [];
    try {
        const hasAccount = account && (new BigNumber(account.toLowerCase())).isGreaterThan(0);
        if (TokenPricesTasks.length) await Promise.all(TokenPricesTasks);
        
        // Get token prices for popular bases
        let tokenPrices = {};
        if (TokenPrices && TokenPrices.timestamp && TokenPrices.timestamp + 60000 >= Date.now()) {
            tokenPrices = TokenPrices
        } else {
            tokenPrices = await retrievePopularTokenPrices();
        }

        const ranches = [];
        const tokens = {};
        const lpTokens = {};
        // Calls 1: Get info from ranch
        const calls1 = [];
        for(let j=0; j<addresses.length; j++) {
            ranches.push({address: addresses[j]});
            calls1.push({
                address: addresses[j],
                name: 'emissionPerBlock'
            }, {
                address: addresses[j],
                name: 'feeAddress'
            }, {
                address: addresses[j],
                name: 'isStarted'
            }, {
                address: addresses[j],
                name: 'isEnded'
            }, {
                address: addresses[j],
                name: 'balance'
            }, {
                address: addresses[j],
                name: 'lpBalance'
            }, {
                address: addresses[j],
                name: 'owner'
            }, {
                address: addresses[j],
                name: 'token'
            }, {
                address: addresses[j],
                name: 'poolInfo'
            }, {
                address: addresses[j],
                name: 'startBlock'
            });
            if (hasAccount) {
                calls1.push({
                    address: addresses[j],
                    name: 'userInfo',
                    params: [account]
                }, {
                    address: addresses[j],
                    name: 'pendingToken',
                    params: [account]
                });
            }
        }
        const c1pl = hasAccount ? 12 : 10;
        // Handle result for calls1
        const res1 = await multicall(abiRanch, calls1);
        for(let k=0; k<addresses.length; k++) {
            Object.assign(ranches[k], {
                emissionPerBlock: new BigNumber(res1[k*c1pl][0]._hex),
                feeAddress: res1[k*c1pl+1][0],
                isStarted: res1[k*c1pl+2][0],
                isEnded: res1[k*c1pl+3][0],
                balance: new BigNumber(res1[k*c1pl+4][0]._hex),
                lpBalance: new BigNumber(res1[k*c1pl+5][0]._hex).div(new BigNumber(10).pow(18)),
                owner: res1[k*c1pl+6][0],
                token: res1[k*c1pl+7][0],
                lpToken: res1[k*c1pl+8][0],
                depositFeeRate: new BigNumber(res1[k*c1pl+8][3]).div(100).toNumber(),
                startBlock: new BigNumber(res1[k*c1pl+9][0]._hex).toNumber()
            });
            if (hasAccount) {
                Object.assign(ranches[k], {
                    userInfo: {
                        stakedLp: new BigNumber(res1[k*c1pl+10][0]._hex).div(new BigNumber(10).pow(18)),
                        pendingToken: new BigNumber(res1[k*c1pl+11][0]._hex)
                    }
                });
            }
            tokens[res1[k*c1pl+7][0]] = true;
            lpTokens[res1[k*c1pl+8][0]] = true;
        }

        // Calls 2: Get LP detail
        const calls2 = [];
        const lpTokenArray = Object.keys(lpTokens);
        lpTokenArray.forEach((lp) => {
            calls2.push({
                address: lp,
                name: 'totalSupply'
            }, {
                address: lp,
                name: 'token0'
            }, {
                address: lp,
                name: 'token1'
            }, {
                address: lp,
                name: 'getReserves'
            });
            if (hasAccount) {
                calls2.push({
                    address: lp,
                    name: 'balanceOf',
                    params: [account],
                })
            }
        });
        const c2pl = hasAccount ? 5 : 4;
        // Handle result for calls2
        const res2 = await multicall(abiPair, calls2);
        for(let l=0; l<lpTokenArray.length; l++) {
            lpTokens[lpTokenArray[l]] = {
                address: lpTokenArray[l],
                totalSupply: new BigNumber(res2[l*c2pl][0]._hex).div(new BigNumber(10).pow(18)),
                token0: res2[l*c2pl+1][0],
                token1: res2[l*c2pl+2][0],
                reserve0:  new BigNumber(res2[l*c2pl+3][0]._hex),
                reserve1:  new BigNumber(res2[l*c2pl+3][1]._hex)
            };
            if(hasAccount) {
                lpTokens[lpTokenArray[l]].userBalance = new BigNumber(res2[l*c2pl+4][0]._hex).div(new BigNumber(10).pow(18));
            }
            tokens[res2[l*c2pl+1][0]] = true;
            tokens[res2[l*c2pl+2][0]] = true;
        }

        // Calls 3: Get token detail
        const calls3 = [];
        const tokenArray = Object.keys(tokens);
        tokenArray.forEach((t) => {
            calls3.push({
                address: t,
                name: 'name'
            }, {
                address: t,
                name: 'symbol'
            }, {
                address: t,
                name: 'decimals'
            }, {
                address: t,
                name: 'totalSupply'
            });
            if (hasAccount) {
                calls3.push({
                    address: t,
                    name: 'balanceOf',
                    params: [account],
                })
            }
        });
        const c3pl = hasAccount ? 5 : 4;
        // Handle result for calls3
        const res3 = await multicall(abiErc20, calls3);
        for(let m=0; m<tokenArray.length; m++) {
            const decimals = new BigNumber(res3[m*c3pl+2][0]).toNumber();
            tokens[tokenArray[m]] = {
                address: tokenArray[m],
                name: res3[m*c3pl][0],
                symbol: res3[m*c3pl+1][0],
                decimals,
                totalSupply: new BigNumber(res3[m*c3pl+3][0]._hex).div(new BigNumber(10).pow(decimals))
            };
            if(hasAccount) {
                tokens[tokenArray[m]].userBalance = new BigNumber(res3[m*c3pl+4][0]._hex).div(new BigNumber(10).pow(decimals));
            }
        }

        // Update LPs, token prices, and ranches
        const tokensMissingPrice = {};
        tokenArray.forEach((tokenAddress) => {
            const token = tokens[tokenAddress];
            if (tokenPrices[tokenAddress]) token.price = tokenPrices[tokenAddress];
        });
        lpTokenArray.forEach((lpAddress)=> {
            const lp = lpTokens[lpAddress];
            const token0 = tokens[lp.token0];
            const token1 = tokens[lp.token1];
            lp.reserve0 = lp.reserve0.div(new BigNumber(10).pow(token0.decimals));
            lp.reserve1 = lp.reserve1.div(new BigNumber(10).pow(token1.decimals));
            if (tokenPrices[lp.token0]) {
                lp.value = tokenPrices[lp.token0].times(lp.reserve0).times(2);
                token0.price = tokenPrices[lp.token0];
                if (!token1.price) token1.price = tokenPrices[lp.token1] || lp.reserve0.div(lp.reserve1).times(token0.price);
            } else if (tokenPrices[lp.token1]) {
                lp.value = tokenPrices[lp.token1].times(lp.reserve1).times(2);
                token1.price = tokenPrices[lp.token1];
                if (!token0.price) token0.price = lp.reserve1.div(lp.reserve0).times(token1.price);
            }
            lp.token0 = token0;
            lp.token1 = token1;
        });
        ranches.forEach((r) => {
            const token = tokens[r.token];
            const lpToken = lpTokens[r.lpToken];
            Object.assign(r, {
                token, lpToken,
                emissionPerBlock: r.emissionPerBlock.div(new BigNumber(10).pow(token.decimals)),
                balance: r.balance.div(new BigNumber(10).pow(token.decimals)),
            });
            if (hasAccount) {
                Object.assign(r.userInfo, {
                    pendingToken: r.userInfo.pendingToken.div(new BigNumber(10).pow(token.decimals)),
                    lpBalance: lpToken.userBalance,
                    tokenBalance: token.userBalance
                });
            }
        });

        const updateLpValueAndTokenPrices = () => {
            lpTokenArray.forEach((lpAddress)=> {
                const lp = lpTokens[lpAddress];
                if (!lp.value) {
                    if (lp.token0.price) {
                        lp.value = lp.token0.price.times(lp.reserve0).times(2);
                        if (!lp.token1.price) {
                            lp.token1.price = lp.reserve0.div(lp.reserve1).times(lp.token0.price);
                        }
                    } else if (lp.token1.price){
                        lp.value = lp.token1.price.times(lp.reserve1).times(2);
                        if (!lp.token0.price) {
                            lp.token0.price = lp.reserve1.div(lp.reserve0).times(lp.token1.price);
                        }
                    }
                }
            });
        }

        // Add tokens without price to the array
        tokenArray.forEach((tokenAddress) => {
            const token = tokens[tokenAddress];
            if (!token.price) tokensMissingPrice[tokenAddress] = token;
        });

        // Try to retrieve missing prices
        if (Object.keys(tokensMissingPrice).length) {
            await tryGetPrices(tokensMissingPrice, tokenPrices);
            updateLpValueAndTokenPrices();
        }

        // Try to retrieve price from global object
        let retrievedAdditionalPrice = false;
        Object.keys(tokensMissingPrice).forEach((tokenAddress) => {
            const token = tokensMissingPrice[tokenAddress];
            if (!token.price && AdditionalTokenPrices[tokenAddress]) {
                retrievedAdditionalPrice = true;
                token.price = AdditionalTokenPrices[tokenAddress];
            }
        });
        if (retrievedAdditionalPrice) updateLpValueAndTokenPrices();

        // Update prices to the global object
        tokenArray.forEach((tokenAddress) => {
            const token = tokens[tokenAddress];
            if (token.price) AdditionalTokenPrices[tokenAddress] = token.price;
        });

        // Update Cache
        ranches.forEach((r) => {
            CACHE[r.address] = {
                data: r,
                timestamp: Date.now()
            }
        });

        return ranches;
    } catch (e) {
        console.error(e);
        return false;
    }
}

export const getMyRanches = async (ranchFactoryContract, userAddress, currentPage = 1) => {
    const maxInPage = 12;
    try {
        const count = await getRanchCountByCreator(ranchFactoryContract, userAddress);
        const pageCount = Math.ceil(count/maxInPage);
        const curPage = currentPage > pageCount ? 1 : currentPage;

        const calls1 = [];
        for(let i=(count-1-(curPage-1)*maxInPage); i>=Math.max(0,(count-curPage*maxInPage)); i--) {
            calls1.push({
                address: ranchFactoryContract.options.address,
                name: 'getRanches',
                params: [userAddress, i],
            });
        }
        const res1 = await multicall(abiRanchFactory, calls1);

        const ranches = await getRanches(ranchFactoryContract, res1 && res1.map(r=>r[0]) || [], userAddress);

        return ranches === false ? false : {
            currentPage: curPage,
            pageCount,
            ranches: ranches || [],
            isLoading: false
        };
    } catch (e) {
        console.error(e);
        return false;
    }
}

export const getRecentRanches = async (ranchFactoryContract, userAddress, currentPage = 1) => {
    const maxInPage = 12;
    try {
        const count = await getRanchCount(ranchFactoryContract);        
        const pageCount = Math.ceil(count/maxInPage);
        const curPage = currentPage > pageCount ? 1 : currentPage;

        const calls1 = [];
        for(let i=(count-1-(curPage-1)*maxInPage); i>=Math.max(0,(count-curPage*maxInPage)); i--) {
            calls1.push({
                address: ranchFactoryContract.options.address,
                name: 'getRanchByIndex',
                params: [i],
            });
        }
        const res1 = await multicall(abiRanchFactory, calls1);
        const arr = res1 && res1.map(r=>r[0]) || [];
        const all = {};
        arr.forEach((r)=>{
            all[r]=true;
        });
        if (singleRanch) all[singleRanch] = true;
        const allArray = Object.keys(all);
        allArray.forEach((r,i)=>{
            all[r] = i;
        });

        const ranches = await getRanches(ranchFactoryContract, allArray, userAddress);

        return ranches === false ? false : {
            currentPage: curPage,
            pageCount,
            count,
            ranches: ranches && arr.map(r=>ranches[all[r]]).filter(r=>r) || [],
            isLoading: false
        };
    } catch (e) {
        console.error(e);
        return false;
    }
}

let singleRanch = null;

export const getSingleRanch = async (ranchFactoryContract, address, userAddress) => {
    if (CACHE && CACHE[address] && CACHE[address].timestamp && CACHE[address].timestamp + 20000 >= Date.now()) {
        singleRanch = address;
        return [CACHE[address].data];
    }

    let addresses = [address];
    try {
        const creator = await getCreator(ranchFactoryContract, address);
        if (creator && new BigNumber(creator.toLowerCase()).toNumber() <= 0) addresses = [];
    } catch {
        addresses = [];
    }

    if (!addresses.length) return [];

    singleRanch = address;
    
    return getRanches(ranchFactoryContract, addresses, userAddress) || [];
}

export const transferOwnership = async (ranchContract, newOwner, account) => {
    try {
        return ranchContract.methods
        .transferOwnership(newOwner)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const end = async (ranchContract, account) => {
    try {
        return ranchContract.methods
        .end()
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const deposit = async (ranchContract, amount, account) => {
    try {
        return ranchContract.methods
        .deposit(amount)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const withdraw = async (ranchContract, amount, account) => {
    try {
        return ranchContract.methods
        .withdraw(amount)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const setFeeAddress = async (ranchContract, feeAddress, account) => {
    try {
        return ranchContract.methods
        .setFeeAddress(feeAddress)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

export const updateEmissionRate = async (ranchContract, emissionRate, account) => {
    try {
        return ranchContract.methods
        .updateEmissionRate(emissionRate)
        .send({ ...gasPriceInObj, from: account})
        .on('transactionHash', (tx) => {
            return tx.transactionHash
        })
    } catch (err) {
        return console.error(err)
    }
}

let FavoriteRanches = null;

export const getFavorites = () => {
    if (FavoriteRanches) return FavoriteRanches;

    const s = localStorage.getItem(favoriteCacheKey);
    if (s) {
        const a = JSON.parse(s);
        if (a && a.length) {
            const m = {};
            a.forEach(e => {
               m[e]= true; 
            });
            FavoriteRanches = {
                array: a,
                map: m
            };
            return FavoriteRanches;
        }
    }

    FavoriteRanches = {
        array: [],
        map: {}
    };

    return FavoriteRanches;
}

const updateFavoritesToCache = () => {
    if (FavoriteRanches && FavoriteRanches.array) {
        localStorage.setItem(favoriteCacheKey, JSON.stringify(FavoriteRanches.array));
    }
}

export const addFavorite = (ranch) => {
    if (!FavoriteRanches) {
        FavoriteRanches = getFavorites();
    }
    if (!FavoriteRanches.map[ranch]) {
        FavoriteRanches.array.push(ranch);
        FavoriteRanches.map[ranch] = true;
        updateFavoritesToCache();
    }
}

export const removeFavorite = (ranch) => {
    if (!FavoriteRanches) {
        FavoriteRanches = getFavorites();
    }
    if (FavoriteRanches.map[ranch]) {
        FavoriteRanches.array.splice(FavoriteRanches.array.indexOf(ranch),1);
        FavoriteRanches.map[ranch] = false;
        updateFavoritesToCache();
    }
}

export const isFavorite = (ranch) => {
    const favorites = getFavorites();
    return !!(favorites && favorites.map && favorites.map[ranch]);
}

export const getFavoriteRanches = async (ranchFactoryContract, userAddress, currentPage = 1) => {
    const maxInPage = 12;
    const favorites = getFavorites();
    try {
        const count = favorites.array.length;
        const pageCount = Math.ceil(count/maxInPage);
        const curPage = currentPage > pageCount ? 1 : currentPage;

        const ranches = await getRanches(ranchFactoryContract, favorites.array.slice((curPage-1)*maxInPage, curPage*maxInPage) || [], userAddress);

        return ranches === false ? false : {
            currentPage: curPage,
            pageCount,
            ranches: ranches || [],
            isLoading: false
        };
    } catch (e) {
        console.error(e);
        return false;
    }
}
